
import AuthImage from '@/components/image/AuthImage.vue'
import { LibraryEntry } from '@/store/modules/teamlibrary/types'
import { Namespace } from '@/store/types'
import { Filters } from '@/utils/filters'
import IconClose from '@svg/IconClose.svg'
import { Component, Vue } from 'vue-property-decorator'

declare var consoleError: any

const RECENT_LIMIT = 5

@Component({
  components: {
    AuthImage,
    IconClose,
  },
})
export default class SelectLibraryObjectModal extends Vue {
  private dialogVisible: boolean = false
  private isLoading: boolean = false
  private isConfirming: boolean = false
  private loadError: string = ''
  private entries: LibraryEntry[] = []
  private pendingEntryId: number = -1

  public async show() {
    this.resetState()
    this.dialogVisible = true
    await this.loadRecentEntries()
  }

  public hide() {
    this.dialogVisible = false
  }

  private get canConfirm(): boolean {
    return this.pendingEntryId > 0 && !this.isConfirming
  }

  private closeDialog() {
    this.hide()
    this.resetState()
  }

  private resetState() {
    this.pendingEntryId = -1
    this.isConfirming = false
    this.loadError = ''
  }

  private thumbnailURLMap(value: string): string {
    return Filters.thumbnailURLMap(value)
  }

  private async loadRecentEntries() {
    this.isLoading = true
    this.loadError = ''
    this.entries = []

    try {
      const ok = await this.$store.dispatch(`${Namespace.TeamLibrary}/loadTenantsLibrary`, {
        limit: RECENT_LIMIT,
        sort: 'date_updated,desc',
        page: 1,
        'visibility[eq]': 'tenant',
      })
      if (!ok) {
        this.loadError = 'Could not load your library. Please try again.'
        return
      }
      // Snapshot into local state so subsequent fetches elsewhere (e.g. Library.vue)
      // don't mutate what the user sees in this modal.
      const stateEntries = this.$store.state[Namespace.TeamLibrary].modelsLibrary as LibraryEntry[]
      this.entries = Array.isArray(stateEntries) ? stateEntries.slice(0, RECENT_LIMIT) : []
    } catch (error) {
      consoleError('SelectLibraryObjectModal: failed to load recent entries', error)
      this.loadError = 'Could not load your library. Please try again.'
    } finally {
      this.isLoading = false
    }
  }

  private onRowClick(entry: LibraryEntry) {
    if (this.isConfirming) {
      return
    }
    this.pendingEntryId = entry.Id
  }

  private hasGenerateJigEntryData(entry: LibraryEntry): boolean {
    return !!(
      entry &&
      entry.Item &&
      typeof entry.Item.Id === 'number' &&
      entry.Item.Id >= 0 &&
      typeof entry.Item.ItemUrl === 'string' &&
      entry.Item.ItemUrl.trim().length > 0
    )
  }

  private async confirmSelection() {
    if (!this.canConfirm) {
      return
    }
    const entry = this.entries.find((e) => e.Id === this.pendingEntryId)
    if (!entry) {
      return
    }

    this.isConfirming = true
    try {
      // Hydrate Item if not already present — mirrors Library.vue's openGenerateJig.
      let resolved: LibraryEntry = entry

      if (!this.hasGenerateJigEntryData(resolved)) {
        const ok = await this.$store.dispatch(`${Namespace.TeamLibrary}/loadLibraryEntry`, { Id: entry.Id })
        if (!ok) {
          this.loadError = 'Could not load this object. Please try another.'
          return
        }
        resolved = this.$store.state[Namespace.TeamLibrary].libraryEntry as LibraryEntry
      }

      if (!this.hasGenerateJigEntryData(resolved)) {
        this.loadError = 'Could not load this object. Please try another.'
        return
      }

      this.$emit('select', resolved)
      this.hide()
      this.resetState()
    } finally {
      this.isConfirming = false
    }
  }
}
